/**
 * utils.js
 * Created by calhosh on 4/21/2020.
 */
import React, {useCallback, useEffect, useState} from 'react';
import {
	numberFormattedText,
	applyRound,
	negativeNumberFormattedText
} from '@ey/utils';
import env from './env';
import resources from '../../resources/resources';
import * as constants from './uiconstants';
import NumberFormat from 'react-number-format';
import EYValidation from './eyValidation';
import {schema} from './irisValidationSchema';

import ReactDOMServer from 'react-dom/server';
import CustomTooltip from '../components/KeyPerformanceIndicators/CustomTooltip';
import {isEmpty, unescape} from 'lodash';
import {MsalService} from '@ey/voltron-core';
import config from '../../auth/config';
import RenderText from '../components/RenderText/RenderText';
import DOMPurify from 'dompurify';

let lang = env.getLanguage();
export let currentResource = resources(lang);
export let labels = currentResource.labels;
export let LocalizedUIConstants = constants;

let updateConstantsWithLocale = (prop) => {
	prop.map((p) => {
		p['localizedlabel'] = currentResource[p.label] || p.label;
		return p;
	});
	return prop;
};

/*set user selected language*/
export const setLanguage = (language) => {
	if (language) {
		currentResource = resources(language);
		labels = currentResource.labels;
	}
	let constantCollections = _.pickBy(
		LocalizedUIConstants,
		(c) => _.isArray(c) && _.isObject(c[0])
	);
	LocalizedUIConstants = _.forEach(
		constantCollections,
		updateConstantsWithLocale
	);
};

/**
 * Gets the corresponding user based thousandSeparator, decimalSeparator and thousandsGroupStyle.
 * @param {String} numberFormat The user's specified number format.
 * @returns {object} An object with the corresponding thousandSeparator, decimalSeparator and
 * thousandsGroupStyle values.
 */
export const setThousandSeparator = (numberFormat) => {
	let thousandSeparator = '',
		decimalSeparator = '.',
		thousandsGroupStyle = 'thousand';

	switch (
		numberFormat === undefined || numberFormat === ''
			? currentResource.numberFormats[0].value
			: numberFormat
	) {
		case currentResource.numberFormats[0].value /*'1,234,567,890'*/:
			thousandSeparator = ',';
			decimalSeparator = '.';
			break;
		case currentResource.numberFormats[1].value /*'1.234.567.890'*/:
			thousandSeparator = '.';
			/*Decimal and thousands separator cannot be the same. We dont show decimal, so just change it*/
			decimalSeparator = ',';
			break;
		case currentResource.numberFormats[2]
			.value /*need to figure this out*/ /*'1,23,45,67,890'*/:
			thousandSeparator = ',';
			thousandsGroupStyle = 'lakh';
			break;
		case currentResource.numberFormats[3].value /*'1 234 567 890'*/:
			thousandSeparator = ' ';
			break;
		case currentResource.numberFormats[4].value /*"1'234'567'890"*/:
			thousandSeparator = `'`;
			break;
		case currentResource.numberFormats[5]
			.value /*need to figure this out*/ /*'12,3456,7890'*/:
			thousandSeparator = ',';
			thousandsGroupStyle = 'wan';
			break;
	}

	return {thousandSeparator, decimalSeparator, thousandsGroupStyle};
};

/*set number rounding*/
export const setNumberRounding = (rounding, numberValue) => {
	let roundedNumber = 0;
	switch (
		rounding === undefined || rounding === ''
			? currentResource.roundingOptions[0].value
			: rounding
	) {
		case currentResource.roundingOptions[0].value /*none*/:
			roundedNumber = Math.round(Number(numberValue));
			break;
		case currentResource.roundingOptions[1].value /*thousands*/:
			roundedNumber = Math.round(Number(numberValue) / 1000);
			break;
		case currentResource.roundingOptions[2].value /*millions*/:
			roundedNumber = Math.round(Number(numberValue) / 1000000);
			break;
		case currentResource.roundingOptions[3].value /*lakhs*/:
			roundedNumber = Math.round(Number(numberValue) / 100000);
			break;
		case currentResource.roundingOptions[4].value /*crores*/:
			roundedNumber = Math.round(Number(numberValue) / 10000000);
			break;
	}

	return roundedNumber;
};

export const getRoundedNumber = (numberValue, roundingFormat, decimalScale) => {
	const roundingFormatMap = LocalizedUIConstants.numberFormats.find(
		(element) => element.value === roundingFormat
	).numberFormatMap;
	if (decimalScale) {
		return numberFormattedText(
			roundingFormatMap,
			applyRounding(numberValue, decimalScale)
		);
	} else {
		return numberFormattedText(roundingFormatMap, Math.round(numberValue));
	}
};

export function applyRounding(number, decimalScale) {
	const decimals = decimalScale ? decimalScale : 0;
	if (number >= 99.9 && number < 100) {
		return (0, _.floor)(number, decimals).toFixed(decimalScale);
	} else {
		if (number <= 0.1 && number > 0) {
			return (0, _.ceil)(number, decimals).toFixed(decimalScale);
		} else {
			return (0, _.round)(number, decimals).toFixed(decimalScale);
		}
	}
}

export const getNegativeNumberFormatComponent = (
	negativeFormat,
	thousandSeparator,
	thousandsGroupStyle,
	decimalSeparator,
	numberValue
) => {
	// converting negative number to positive
	numberValue = Math.abs(numberValue);

	if (
		negativeFormat === undefined ||
		negativeFormat === '' ||
		negativeFormat ===
			currentResource.negativeNumberFormats[0].value /*'(1.1)'*/
	) {
		return (
			<NumberFormat
				value={numberValue}
				prefix={'('}
				suffix={')'}
				displayType="text"
				thousandSeparator={thousandSeparator}
				thousandsGroupStyle={thousandsGroupStyle}
				decimalSeparator={decimalSeparator}
			/>
		);
	}
	if (
		negativeFormat === currentResource.negativeNumberFormats[1].value /*'-1.1'*/
	) {
		return (
			<NumberFormat
				value={numberValue}
				prefix={'-'}
				displayType="text"
				thousandSeparator={thousandSeparator}
				thousandsGroupStyle={thousandsGroupStyle}
				decimalSeparator={decimalSeparator}
			/>
		);
	}
	if (
		negativeFormat ===
		currentResource.negativeNumberFormats[2].value /*'- 1.1'*/
	) {
		return (
			<NumberFormat
				value={numberValue}
				prefix={'- '}
				displayType="text"
				thousandSeparator={thousandSeparator}
				thousandsGroupStyle={thousandsGroupStyle}
				decimalSeparator={decimalSeparator}
			/>
		);
	}
	if (
		negativeFormat === currentResource.negativeNumberFormats[3].value /*'1.1-'*/
	) {
		return (
			<NumberFormat
				value={numberValue}
				suffix={'-'}
				displayType="text"
				thousandSeparator={thousandSeparator}
				thousandsGroupStyle={thousandsGroupStyle}
				decimalSeparator={decimalSeparator}
			/>
		);
	}
	if (
		negativeFormat ===
		currentResource.negativeNumberFormats[4].value /*'1.1 -'*/
	) {
		return (
			<NumberFormat
				value={numberValue}
				suffix={' -'}
				displayType="text"
				thousandSeparator={thousandSeparator}
				thousandsGroupStyle={thousandsGroupStyle}
				decimalSeparator={decimalSeparator}
			/>
		);
	}
};

export const getNegativeNumber = (
	numberFormat,
	negativeFormat,
	number,
	isRounding = false
) => {
	const roundingFormatMap = LocalizedUIConstants.numberFormats.find(
		(element) => element.value === numberFormat
	).numberFormatMap;
	return isRounding
		? negativeNumberFormattedText(
				negativeFormat,
				roundingFormatMap,
				Math.round(Math.abs(number))
		  )
		: negativeNumberFormattedText(
				negativeFormat,
				roundingFormatMap,
				Math.abs(number)
		  );
};

export const getFormattedRoundedHours = (
	hrsCharged,
	numberFormat,
	negativeNumberFormat
) => {
	if (hrsCharged >= 0) {
		//show rounded value upto 1 decimal place.
		let roundedHrsChargedValue = getRoundedNumber(hrsCharged, numberFormat, 1);

		// we dont want to show .0 in decimal
		if (roundedHrsChargedValue - Math.floor(roundedHrsChargedValue) == 0) {
			return Math.round(roundedHrsChargedValue);
		}

		return roundedHrsChargedValue;
	} else {
		return getNegativeNumber(numberFormat, negativeNumberFormat, hrsCharged);
	}
};

export const getNumberFormatId = (numberFormat) => {
	return LocalizedUIConstants.numberFormats
		.filter((f) => {
			return numberFormat === f.value;
		})
		.pop().numberFormatMap;
};

/* Get username (givenname + family name) using msal account */
export const getUserName = () => {
	const account = new MsalService(config).getActiveAccount();
	return account?.name;
};

export const returnRoundingFormatLocalizedVal = (roundingFormat) => {
	let localizedRoundingFormat = '';
	switch (roundingFormat) {
		case currentResource.roundingOptions[0].value /*none*/:
			localizedRoundingFormat = labels.none;
			break;
		case currentResource.roundingOptions[1].value /*thousands*/:
			localizedRoundingFormat = labels.thousands;
			break;
		case currentResource.roundingOptions[2].value /*millions*/:
			localizedRoundingFormat = labels.millions;
			break;
		case currentResource.roundingOptions[3].value /*lakhs*/:
			localizedRoundingFormat = labels.lakhs;
			break;
		case currentResource.roundingOptions[4].value /*crores*/:
			localizedRoundingFormat = labels.crores;
			break;
	}
	return localizedRoundingFormat;
};

/**
 * Normalizes a collection of elements based on redux's docs recommendations.
 * https://redux.js.org/recipes/structuring-reducers/normalizing-state-shape
 * @param {Array} value A collection of elements to be normalized.
 * @param {String} key A string representing the element's id field name.
 * @param {object} initialState An initial state with a normalized form.
 * @returns {object} An object with the normalized state.
 */
export function normalize(
	value,
	key = 'id',
	initialState = undefined,
	skipKeyDelete = false
) {
	let result = {
		byId: {},
		allIds: []
	};
	if (initialState) {
		result.byId = {...initialState.byId};
		result.allIds = [...initialState.allIds];
	}

	if (
		!(
			value === null ||
			value === undefined ||
			(Array.isArray(value) && !value.length > 0)
		)
	) {
		for (let i = 0; i < value.length; i++) {
			const element = value[i];

			let id = element[key];

			if (!result.byId[id]) {
				result.allIds.push(id);
			}

			result.byId[id] = {
				...result.byId[id],
				...element,
				id
			};

			if (key !== 'id' && !skipKeyDelete) {
				delete result.byId[id][key];
			}
		}
	}

	return result;
}

const validator = new EYValidation(schema);
/**
 * Validates a field using Iris' corresponding validation schema.
 * @param {string} name The field's name, a key to be used in the consumer component's state.
 * @param {string} value The field's value.
 * @param {Function} setState A function that updates the consumer component's state.
 * @returns {boolean} A value indicating wheter the passed value is valid.
 */
const validate = (name, value, setState) => {
	let isValid = validator.ValidateField(name, value);

	setState((state) => ({
		...state,
		[name]: {value, isValid}
	}));

	return isValid;
};

/**
 * Creates a normalized slice with the filter structure.
 * @param {Array} collection The collection of filters.
 * @param {Function} selector A selector function.
 * @returns {object} The normalized filter object.
 */
export function filter(collection, selector) {
	return {
		byId: Object.fromEntries(
			collection.map((entity) => [selector(entity), true])
		),
		allIds: collection.map(selector)
	};
}

/**
 * Creates a normalized slice with the filter structure.
 * @param {Array} collection The collection of filters.
 * @param {Function} selector A selector function.
 * @param {Function} storekey key to retrive from session storage.
 * @returns {object} The normalized filter object.
 */
export function localStoreFiltersForClients(collection, selector, storekey) {
	return {
		byId: Object.fromEntries(
			collection.map((entity) => [
				selector(entity),
				sessionStorage.getItem(storekey + selector(entity)) || true
			])
		),
		allIds: collection.map(selector)
	};
}

/**
 * Creates a normalized slice with the filter structure.
 * @param {Array} collection The collection of filters.
 * @param {Function} selector A selector function.
 * @param {Function} storekey key to retrive from session storage.
 * @param {Function} parentStorekey key to retrive from session storage.
 * @returns {object} The normalized filter object.
 */
export function localStoreFiltersForFinancialCodes(
	collection,
	selector,
	storekey,
	parentStoreKey
) {
	return {
		byId: Object.fromEntries(
			collection.map((entity) => [
				selector(entity),
				sessionStorage.getItem(storekey + selector(entity)) ||
					getParentFilterForFinancialCode(entity, parentStoreKey) ||
					true
			])
		),
		allIds: collection.map(selector)
	};
}

export function getParentFilterForFinancialCode(entity, parentStoreKey) {
	let storeKey = '';
	let storeValue = null;
	//Check if client(parent of financialCodes) is checked/unchecked.
	storeKey = parentStoreKey + entity.financialClientId;
	storeValue = sessionStorage.getItem(storeKey);
	return storeValue;
}

/**
 * Creates a normalized slice with the filter structure.
 * @param {Array} collection The collection of filters.
 * @param {Function} selector A selector function.
 * @param {Function} storekey key to retrive from session storage.
 * @param {Array} canvasEngagementData The collection of canvasEngagementData.
 * @param {Array} projectSummary The collection of projectSummary.
 * @returns {object} The normalized filter object.
 */
export function localStoreFiltersForCanvasEngagements(
	collection,
	selector,
	storekey,
	parentStoreKey,
	canvasEngagementData,
	projectSummary
) {
	const entries = collection.map((entity) => {
		const hasStorageKey = sessionStorage.getItem(storekey + selector(entity));

		const hasParentItem = getParentFilterForCanvasEngagements(
			entity,
			parentStoreKey,
			canvasEngagementData,
			projectSummary
		);

		return [selector(entity), hasStorageKey || hasParentItem || true];
	});

	return {
		byId: Object.fromEntries(entries),
		allIds: collection.map(selector)
	};
}

export function getParentFilterForCanvasEngagements(
	entity,
	parentStoreKey,
	canvasEngagementData,
	projectSummary
) {
	let storeKey = '';
	let storeValue = null;

	let financialEngagementId = canvasEngagementData.find(
		(f) => f.id === entity.id
	)?.financialEngagementId;

	let financialClientId =
		financialEngagementId &&
		projectSummary.financialCodes.find(
			(f) => f.financialEngagementId === financialEngagementId
		)?.financialClientId;

	//Check if client(parent of financialCodes) is checked/unchecked.
	storeKey =
		parentStoreKey + constants.storageKeys.clientsKey + financialClientId;
	storeValue = sessionStorage.getItem(storeKey);

	//If client is checked. If no, uncheck all financialCodes & canvasEngagements
	if (storeValue === true) {
		//If yes, check if financialCode is checked/unchecked
		storeKey =
			parentStoreKey +
			constants.storageKeys.financialCodesKey +
			financialEngagementId;
		storeValue = sessionStorage.getItem(storeKey);
	}
	return storeValue;
}

/**
 * Creates a normalized entry based on an entity and a initial state.
 * @param {object} entity The entity to be created.
 * @param {object} state The current state.
 * @param {string} key The entity's key name.
 * @returns {object} A normalized updated state object.
 */
export function nCreate(entity, state, key = 'id') {
	let result;
	if (!state || isEmpty(state)) {
		result = normalize();
	} else {
		result = {
			...state,
			byId: {
				...state.byId
			}
		};
	}

	const id = entity[key];
	if (!result.byId[id]) {
		result.allIds.push(id);
	}

	result.byId[id] = {
		...entity,
		id
	};

	if (key !== 'id') {
		delete result.byId[id][key];
	}

	return result;
}

/**
 * Gets an entry from a normalized slice.
 * @param {any} id The object's key.
 * @param {object} state The normalized slice.
 * @returns {object} The entry object or undefined if not found.
 */
export function read(id, state) {
	return state?.byId[id];
}

/**
 * Updates a normalized entry based on an entity and a initial state.
 * @param {any} id The entry's Id.
 * @param {object} entity The entity to be used as patch.
 * @param {object} state A normalized updated state object.
 * @returns {object} A normalized updated state object.
 */
export function nUpdate(id, entity, state) {
	let result;
	if (!state || isEmpty(state)) {
		result = normalize();

		result.byId[id] = {
			...entity,
			id
		};
		result.allIds.push(id);
	} else {
		result = {
			...state,
			byId: {
				...state.byId,
				[id]: {
					...state.byId[id],
					...entity
				}
			}
		};
	}
	return result;
}

/**
 * Deletes a normalized entry based on an entity and a initial state.
 * @param {any} id The entry's Id.
 * @param {object} state A normalized updated state object.
 * @returns {object} A normalized updated state object.
 */
export function nDelete(id, state) {
	if (state.byId[id]) {
		const result = {
			...state,
			allIds: state.allIds.filter((item) => item !== id)
		};
		if (result.allIds.length > 0) {
			delete result.byId[id];
		} else {
			return undefined;
		}
		return result;
	}
	return state;
}

/**
 * Provides a form values and validation state object, and a validation callback.
 * @param {object} initialState The form's initial state.
 * @returns {Array} An array containing the state object, and a useCallback function that validates
 * the passed value and sets the consumer component's state accoringly.
 */
export function useForm(initialState = {}) {
	const [state, setState] = useState(initialState);
	return [
		state,
		useCallback((name, value) => validate(name, value, setState), []),
		useCallback(() => setState(initialState), [])
	];
}

/**
 * Provides wizard navigation previous navigation basic implementation.
 * @param {boolean} previous A value indicating wheter the previous step was selected.
 * @param {Function} onPrevious The callback that the Wizard component passed to its children components.
 */
export function usePrevious(previous, onPrevious) {
	useEffect(() => {
		if (previous && onPrevious) {
			onPrevious(previous);
		}
	}, [previous]);
}

/**
 * Provides wizard navigation next navigation basic implementation.
 * @param {boolean} next A value indicating wheter the next step was selected.
 * @param {object} state The current form's state.
 * @param {Function} validate A callback to perform validations.
 * @param {Function} onNext The callback that the Wizard component passed to its children components.
 */
export function useNext(next, state, validate, onNext) {
	useEffect(() => {
		if (next) {
			onNext(validateForm(state, validate));
		}
	}, [next]);
}

/**
 * Validates the specified form's state using the provided validate callback.
 * @param {object} state An object containing all the key value pairs in the form.
 * @param {Function} validate The function returned by the useForm custom hook.
 * @returns {boolean} A value indicating wheter the form is valid.
 */
export function validateForm(state, validate) {
	let valid = true;
	Object.entries(state).forEach((entry) => {
		const name = entry[0],
			value = entry[1].value;

		if (!validate(name, value)) {
			valid = false;
		}
	});
	return valid;
}

export function calculatePercentage(numerator, denominator, decimals = 0) {
	if (denominator == 0) {
		return 0;
	}

	var percentage = (numerator * 100) / denominator;
	return applyRound(percentage, decimals);
}

export function calculateMargin(ansr, cost, decimals = 0) {
	if (ansr === 0) {
		return 0;
	}

	var margin = ((ansr - cost) * 100) / ansr;
	return applyRound(margin, decimals);
}

export function replaceDecimalSeparator(decimalValue, decimalSeparator) {
	return decimalValue.toString().replace('.', decimalSeparator);
}

export function customDivide(numerator, denominator) {
	if (denominator === 0) {
		return 0;
	}

	return numerator / denominator;
}

export function getHtmlTextElement(text) {
	return <span title={text} dangerouslySetInnerHTML={createMarkup(text)} />;
}

/**
 * Private function to return html required for setting dangerous inner html
 * @param {string} text - the text to display
 * @returns {Object} an object with html set to the text passed in
 * */
export function createMarkup(text) {
	const sanitizedText = getSanitizedText(text, {encode: false})
	return {__html: sanitizedText};
}

/**
 * Get a sanitized text after parsing input for XSS.
 * @param {string} text - the input text
 * @returns {string} sanitized text
 * */
export function getSanitizedText(text, { encode = false }) {
	const sanitizer = DOMPurify.sanitize;
	let sanitizedText = unescape(sanitizer(text));
	if (encode) {
		sanitizedText = encodeURIComponent(sanitizedText);
	}
	return sanitizedText;
}

export function formatNumber(
	value,
	minimumIntegerDigits,
	minimumFractionDigits
) {
	var format = new Intl.NumberFormat('en-US', {
		minimumIntegerDigits: minimumIntegerDigits,
		minimumFractionDigits: minimumFractionDigits
	});

	return format.format(value);
}

/**
 * Renders a component to html so it can be passed as a prop to Highcharts components.
 * @param {Function} tooltip A component to render.
 * @param {string} theme The actual theme.
 * @param {boolean} tooltipOverlap A value indicating wheter the tooltip should overlay over its parent.
 * @returns {object} A configuration object for Highcharts.
 */
export function applyTooltip(
	tooltip,
	theme,
	tooltipOverlap = false,
	borderColor
) {
	let _tooltip = {
		enabled: false
	};

	if (tooltip !== undefined) {
		_tooltip = {
			enabled: true,
			borderColor: borderColor ? borderColor : '#2e2e38',
			formatter: function() {
				// The highcharts formatter expects a string containing an HTML
				// https://api.highcharts.com/highcharts/tooltip.formatter
				// For rendering the passed component, ReactDOMServer is used for simplicity, but
				// ReactDOM.render in a unattached node can be done as well.
				// https://reactjs.org/docs/react-dom-server.html
				return ReactDOMServer.renderToString(
					<CustomTooltip theme={theme} {...this}>
						{tooltip}
					</CustomTooltip>
				);
			},
			// TODO: Small charts tooltips do not overflow, we need to set this to true and update styling
			outside: tooltipOverlap,
			followPointer: true,
			/*positioner: function(width, height, point) {
				return {
					x: point.plotX + this.now.anchorX,
					y: point.plotY - this.now.anchorY
				};
			},*/
			useHTML: true
		};
	}

	return _tooltip;
}

/**
 * Returns the height and width for highcharts based on string values.
 * @param {String} size The desired size.
 * @returns {number} A value to be applied in width and height for chars.
 */
// export function applySize(size) {
// 	switch (size) {
// 		case 'tiny':
// 			return 50;
// 		case 'small':
// 			return 100;
// 		case 'medium':
// 			return 150;
// 		case 'large':
// 			return 200;
// 		case 'x-large':
// 			return 300;
// 		default:
// 			return 150;
// 	}
// }

export function applySize(overrideSize) {
	let size = overrideSize ? overrideSize : 'medium';
	if (!overrideSize) {
		const winSize = window.innerWidth;
		winSize > 1281 ? (size = 'large') : (size = 'medium');
	}
	switch (size) {
		case 'tiny':
			return 50;
		case 'small':
			return 100;
		case 'medium':
			return 150;
		case 'large':
			return 200;
		case 'x-large':
			return 300;
		default:
			return 150;
	}
}

export function convertUserPreferencesDateFormatToMoment(userFormat) {
	switch (userFormat) {
		case 'MM/dd/yyyy':
			return 'MM/DD/YYYY';
		case 'dd/MM/yyyy':
			return 'DD/MM/YYYY';
		case 'yyyy/MM/dd':
			return 'YYYY/MM/DD';
	}
}

export function convertUserPreferencesDateFormatWithTimeZoneToMoment(
	userFormat
) {
	switch (userFormat) {
		case 'MM/dd/yyyy':
			return 'MM/DD/YYYY HH:MM:SS';
		case 'dd/MM/yyyy':
			return 'DD/MM/YYYY HH:MM:SS';
		case 'yyyy/MM/dd':
			return 'YYYY/MM/DD HH:MM:SS';
	}
}

export function convertUserPreferencesDateFormatToExcel(userFormat) {
	switch (userFormat) {
		case 'MM/dd/yyyy':
			return 'mm/dd/yyyy;@';
		case 'dd/MM/yyyy':
			return 'dd/mm/yyyy';
		case 'yyyy/MM/dd':
			return 'yyyy/mm/dd;@';
	}
}

export function convertUserPreferencesDateFormatToIds(userFormat) {
	switch (userFormat) {
		case 'MM/dd/yyyy':
			return 0;
		case 'dd/MM/yyyy':
			return 1;
		case 'yyyy/MM/dd':
			return 2;
	}
}

export function convertUserPreferencesDateFormatToHighcharts(
	userFormat,
	twoDigitYear = false
) {
	switch (userFormat) {
		case 'MM/dd/yyyy':
			return twoDigitYear ? '%m/%d/%y' : '%m/%d/%Y';
		case 'dd/MM/yyyy':
			return twoDigitYear ? '%d/%m/%y' : '%d/%m/%Y';
		case 'yyyy/MM/dd':
			return twoDigitYear ? '%y/%m/%d' : '%Y/%m/%d';
	}
}

export function groupBy(array, key) {
	// Return the end result
	return array.reduce((result, currentValue) => {
		// If an array already present for key, push it to the array. Else create an array and push the object
		(result[currentValue[key]] = result[currentValue[key]] || []).push(
			currentValue
		);
		// Return the current iteration `result` value, this will be taken as next iteration `result` value and accumulate
		return result;
	}, {}); // empty object is the initial value for result object
}

/**
 * Filters an item based on a lower case search criteria.
 * @param {string} value The current item's value.
 * @param {string} searchString The lower case search string.
 * @returns {bool} a value indicating whether the item matches the specified criteria.
 */
export function lowerCaseSearch(value, searchString) {
	return !searchString || value?.toLowerCase().includes(searchString);
}

/**
 * Returns a string version of an entity's name using its Id and description.
 * @param {any} code The entity's Id.
 * @param {string} name The entity's description.
 * @returns {string} The calculated entity's description.
 */
export function getCodeName(code, name) {
	return `${name} (${code})`;
}

/**
 * Provides a wrapper for ag-grid row sorting.
 * https://www.ag-grid.com/javascript-grid/row-sorting/
 * @param {*} valueA The values in the cells to be compared. Typically sorts are done on these values only.
 * @param {*} valueB The values in the cells to be compared. Typically sorts are done on these values only.
 * @param {object} nodeA The Row Nodes for the rows getting sorted. These can be used if more information, such as data from other columns, are needed for the comparison.
 * @param {object} nodeB The Row Nodes for the rows getting sorted. These can be used if more information, such as data from other columns, are needed for the comparison.
 * @param {bool} isInverted true for Ascending, false for Descending.
 * @returns {number} A number with the comparison result.
 */
export function comparator(
	valueA,
	valueB,
	nodeA = undefined,
	nodeB = undefined,
	isInverted = undefined
) {
	if (valueA.label > valueB.label) {
		return 1;
	} else {
		if (valueB.label > valueA.label) {
			return -1;
		} else {
			return 0;
		}
	}
}

export function comparatorForSelect(valueA, valueB) {
	if (valueA.value > valueB.value) {
		return 1;
	} else {
		if (valueB.value > valueA.value) {
			return -1;
		} else {
			return 0;
		}
	}
}

export function ComparatorForDate(a, b) {
	let date1 = a.value;
	let date2 = b.value;

	if (date1 === '-') {
		return -1;
	}
	if (date2 === '-') {
		return 1;
	}

	const parts1 = date1.split('/');
	const parts2 = date2.split('/');

	const month1 = parseInt(parts1[0], 10) - 1;
	const day1 = parseInt(parts1[1], 10);
	const year1 = parseInt(parts1[2], 10);

	const month2 = parseInt(parts2[0], 10) - 1;
	const day2 = parseInt(parts2[1], 10);
	const year2 = parseInt(parts2[2], 10);

	const dateObj1 = new Date(year1, month1, day1);
	const dateObj2 = new Date(year2, month2, day2);

	if (dateObj1 < dateObj2) {
		return -1;
	} else if (dateObj1 > dateObj2) {
		return 1;
	} else {
		return 0;
	}
}

export function ComparatorForOriginalValue(value1, value2) {
	if (value1.originalValue > value2.originalValue) {
		return 1;
	} else {
		if (value2.originalValue > value1.originalValue) {
			return -1;
		} else {
			return 0;
		}
	}
}

export function hasValue(value) {
	let result = false;

	switch (typeof value) {
		case 'object':
			if (Array.isArray(value)) {
				if (value.length > 0) {
					result = true;
				}
			} else {
				result = !_.isEmpty(value);
			}
			break;
		case 'string':
			if (value !== undefined && value !== null && value.trim() !== '') {
				result = true;
			}
			break;
		default:
			if (value !== undefined && value !== null) {
				result = true;
			}
			break;
	}

	return result;
}

export function getTheme() {
	return document
		.getElementsByTagName('body')[0]
		.getAttribute('motif-theme') === 'light'
		? null
		: 'dark';
}

export function getFinancialCodeWithoutPrefix(code) {
	if (code && code.indexOf('-') > -1) {
		return code.split('-')[1];
	}

	return code;
}

export function getIndependenceKey(id = '', type) {
	const splits = id.split('_');
	switch (type) {
		case constants.independenceKeyType.EngagementTeamMember:
			return `${splits[1]}_${splits[2]}`;
		default:
			return id;
	}
}

export function getFormattedNumber(value, userPreferences) {
	let numberValue = setNumberRounding(userPreferences.roundingFormat, value);
	return numberValue === 0 ? (
		0
	) : numberValue > 0 ? (
		<NumberFormat
			value={numberValue}
			displayType="text"
			thousandSeparator={userPreferences.thousandSeparator}
			thousandsGroupStyle={userPreferences.thousandsGroupStyle}
			decimalSeparator={userPreferences.decimalSeparator}
			renderText={RenderText}
		/>
	) : (
		getNegativeNumberFormatComponent(
			userPreferences.negativeNumberFormat,
			userPreferences.thousandSeparator,
			userPreferences.thousandsGroupStyle,
			userPreferences.decimalSeparator,
			numberValue
		)
	);
}

export function canvasEngagementsComparer(eng1, eng2) {
	if (eng1.engagementDescription > eng2.engagementDescription) {
		return 1;
	} else if (eng1.engagementDescription < eng2.engagementDescription) {
		return -1;
	} else {
		return 0;
	}
}

export function sortComparator(item1, item2, key) {
	const c1 = item1[key].toString().trim();
	const c2 = item2[key].toString().trim();

	if (c1 > c2) {
		return 1;
	} else if (c1 < c2) {
		return -1;
	} else {
		return 0;
	}
}

export function getErrorMessage(result, showErrorNumber) {
	let displayError =
		result &&
		result.response &&
		result.response.data &&
		result.response.data.errors &&
		result.response.data.errors.Body &&
		result.response.data.errors.Body.length > 0 &&
		result.response.data.errors.Body[0];

	return displayError !== undefined ? displayError : '';
}

export function getErrorMessageFromResponse(responseMessage) {
	let error;
	try {
		if (responseMessage && responseMessage.indexOf(' - ') > 0) {
			error = responseMessage.substr(0, responseMessage.indexOf(' -'));
		}
	} catch (err) {}
	if (error && error.length > 0 && !Array.isArray(error)) {
		if (!error.ErrorCode) {
			const errorValue = Number(error);
			if (!isNaN(errorValue)) {
				return currentResource.Errors[errorValue]
					? error + ': ' + currentResource.Errors[errorValue]
					: responseMessage;
			}
		}
	}

	return responseMessage;
}

/**
 * Check if a reference is null or undefined. Plus check for stringified null/undefined.
 * @param {*} value reference to check.
 * @returns {Bool} true if null/undefined/'null'/'undefined'
 */
export function isNullOrUndefined(value) {
	return value === null || value === undefined;
}

/**
 * Check if a function reference is valid.
 * @param {Function} fn function referece to check.
 * @returns {Bool} true if function is valid, false if not.
 */
export function isValidFunction(fn) {
	return !isNullOrUndefined(fn) && typeof fn === 'function';
}
